import { useState, useRef, useEffect } from "react";
import PhoneInput from "react-phone-input-2";
import "react-phone-input-2/lib/style.css";
import { X } from "lucide-react";
import axios from "axios";

const AddressForm = ({
  formData,
  setFormData,
  onClose,
  onSubmit,
  addresses,
}) => {
  const [formError, setFormError] = useState("");
  const [loading, setLoading] = useState(false);

  const [countries, setCountries] = useState([]);
  const [states, setStates] = useState([]);
  const [cities, setCities] = useState([]);

  const formRef = useRef(null);

  const authToken = localStorage.getItem("authToken");
  const userId = localStorage.getItem("authUserId");

  // 🔹 Close form on outside click
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (formRef.current && !formRef.current.contains(event.target)) {
        onClose();
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, [onClose]);

  // 🔹 Fetch countries
  useEffect(() => {
    const fetchCountries = async () => {
      try {
        const res = await axios.get(
          "https://tyka.premierhostings.com/backend/api/countries",
          {
            headers: { Authorization: `Bearer ${authToken}` },
          }
        );
        setCountries(res.data.data || []);
      } catch (error) {
        console.error("❌ Error fetching countries:", error);
      }
    };
    fetchCountries();
  }, [authToken]);

  // 🔹 Fetch states
  useEffect(() => {
    if (!formData.country) return;
    const fetchStates = async () => {
      try {
        const res = await axios.get(
          `https://tyka.premierhostings.com/backend/api/states/${formData.country}`,
          {
            headers: { Authorization: `Bearer ${authToken}` },
          }
        );
        setStates(res.data.data || []);
        setCities([]);
      } catch (error) {
        console.error("❌ Error fetching states:", error);
      }
    };
    fetchStates();
  }, [formData.country, authToken]);

  // 🔹 Fetch cities
  useEffect(() => {
    if (!formData.state) return;
    const fetchCities = async () => {
      try {
        const res = await axios.get(
          `https://tyka.premierhostings.com/backend/api/cities/${formData.state}`,
          {
            headers: { Authorization: `Bearer ${authToken}` },
          }
        );
        setCities(res.data.data || []);
      } catch (error) {
        console.error("❌ Error fetching cities:", error);
      }
    };
    fetchCities();
  }, [formData.state, authToken]);

  // 🔹 Handle Submit
  const handleSubmit = async (e) => {
    e.preventDefault();
    setFormError("");
    setLoading(true);

    // Get country/state/city names from IDs
    const countryName =
      countries.find((c) => c.id === parseInt(formData.country))?.name || "";
    const stateName =
      states.find((s) => s.id === parseInt(formData.state))?.name || "";
    const cityName =
      cities.find((c) => c.id === parseInt(formData.city))?.name || "";

    // Payloads
    const apiPayload = {
      user_id: userId,
      name: formData.name,
      email: formData.email,
      street: formData.line1,
      apartment_suite: formData.landmark,
      zipcode: formData.pincode,
      country_id: formData.country,
      state_id: formData.state,
      city_id: formData.city,
      status: 1,
      address_type: formData.addressType,
      phone_number: formData.contact,
    };

    const guestPayload = {
      guest_name: formData.name,
      guest_email: formData.email,
      guest_street: formData.line1,
      guest_apartment_suite: formData.landmark,
      guest_zipcode: formData.pincode,

      guest_country_id: formData.country,
      guest_country_name: countryName,

      guest_state_id: formData.state,
      guest_state_name: stateName,

      guest_city_id: formData.city,
      guest_city_name: cityName,

      guest_status: 1,
      guest_address_type: formData.addressType,
      guest_phone_number: formData.contact,
    };

    // Submit logic
    if (userId && authToken) {
      try {
        const res = await axios.post(
          "https://tyka.premierhostings.com/backend/api/user-addresses",
          apiPayload,
          {
            headers: { Authorization: `Bearer ${authToken}` },
          }
        );
        console.log("✅ API Response:", res.data);
        if (onSubmit) onSubmit(res.data);
        onClose();
      } catch (error) {
        console.error("❌ API error:", error);
        setFormError("Failed to create address. Try again.");
      }
    } else {
      // Save guest address to localStorage
      localStorage.setItem("guestAddress", JSON.stringify(guestPayload));
      console.log("📦 Guest data saved to localStorage");
      if (onSubmit) onSubmit(guestPayload); // Optional: Notify parent
      onClose();
    }

    setLoading(false);
  };

  return (
    <div className="fixed inset-0 z-50 flex justify-end bg-black bg-opacity-40">
      <div
        ref={formRef}
        className="w-full max-w-[410px] bg-white h-full p-6 shadow-lg overflow-auto relative"
      >
        <div className="flex justify-between items-center mb-4">
          <h3 className="text-lg font-bold">Add Delivery Address</h3>
          <button onClick={onClose}>
            <X />
          </button>
        </div>

        {formError && <p className="text-red-500 text-sm mb-2">{formError}</p>}

        <form onSubmit={handleSubmit} className="space-y-3">
          <label className="block text-sm font-medium mb-1">
            Full Name <span className="text-red-500">*</span>
          </label>
          <input
            type="text"
            placeholder="Full Name"
            className="w-full border px-3 py-2 rounded text-sm"
            value={formData.name}
            onChange={(e) => setFormData({ ...formData, name: e.target.value })}
            required
          />

          <label className="block text-sm font-medium mb-1">
            Email Address <span className="text-red-500">*</span>
          </label>
          <input
            type="email"
            placeholder="Email Address"
            className="w-full border px-3 py-2 rounded text-sm"
            value={formData.email}
            onChange={(e) =>
              setFormData({ ...formData, email: e.target.value })
            }
            required
          />

          <label className="block text-sm font-medium mb-1">
            Address <span className="text-red-500">*</span>
          </label>
          <input
            type="text"
            placeholder="Address Line 1"
            className="w-full border px-3 py-2 rounded text-sm"
            value={formData.line1}
            onChange={(e) =>
              setFormData({ ...formData, line1: e.target.value })
            }
            required
          />

          <label className="block text-sm font-medium mb-1">
            Country <span className="text-red-500">*</span>
          </label>
          <select
            className="w-full border px-3 py-2 rounded text-sm"
            value={formData.country}
            onChange={(e) =>
              setFormData({
                ...formData,
                country: e.target.value,
                state: "",
                city: "",
              })
            }
            required
          >
            <option value="">
              Select Country <span className="text-red-500">*</span>
            </option>
            {countries.map((c) => (
              <option key={c.id} value={c.id}>
                {c.name}
              </option>
            ))}
          </select>

          <label className="block text-sm font-medium mb-1">
            State <span className="text-red-500">*</span>
          </label>
          <select
            className="w-full border px-3 py-2 rounded text-sm"
            value={formData.state}
            onChange={(e) =>
              setFormData({ ...formData, state: e.target.value, city: "" })
            }
            required
          >
            <option value="">Select State</option>
            {states.map((s) => (
              <option key={s.id} value={s.id}>
                {s.name}
              </option>
            ))}
          </select>

          <label className="block text-sm font-medium mb-1">
            City <span className="text-red-500">*</span>
          </label>
          <select
            className="w-full border px-3 py-2 rounded text-sm"
            value={formData.city}
            onChange={(e) => setFormData({ ...formData, city: e.target.value })}
            required
          >
            <option value="">Select City</option>
            {cities.map((c) => (
              <option key={c.id} value={c.id}>
                {c.name}
              </option>
            ))}
          </select>

          <label className="block text-sm font-medium mb-1">
            Pincode <span className="text-red-500">*</span>
          </label>
          <input
            type="text"
            placeholder="Pincode"
            className="w-full border px-3 py-2 rounded text-sm"
            value={formData.pincode}
            onChange={(e) =>
              setFormData({ ...formData, pincode: e.target.value })
            }
            required
          />

          <label className="block text-sm font-medium mb-1">
            Contact Number <span className="text-red-500">*</span>
          </label>
          <PhoneInput
            country={"in"}
            inputStyle={{ width: "100%" }}
            value={formData.contact}
            onChange={(value) => setFormData({ ...formData, contact: value })}
            inputProps={{ required: true }}
          />

          <label className="block text-sm font-medium mb-1">
            Landmark <span className="text-red-500">*</span>
          </label>
          <input
            type="text"
            placeholder="Landmark"
            className="w-full border px-3 py-2 rounded text-sm"
            value={formData.landmark}
            onChange={(e) =>
              setFormData({ ...formData, landmark: e.target.value })
            }
            required
          />

          <label className="block text-sm font-medium mb-1">
            Address Type <span className="text-red-500">*</span>
          </label>
          <div className="flex gap-4">
            {["Home", "Office"].map((type) => (
              <label key={type} className="flex items-center gap-2 text-sm">
                <input
                  type="radio"
                  name="addressType"
                  value={type.toLowerCase()}
                  checked={formData.addressType === type.toLowerCase()}
                  onChange={(e) =>
                    setFormData({ ...formData, addressType: e.target.value })
                  }
                  required
                />
                {type}
              </label>
            ))}
          </div>

          <button
            type="submit"
            disabled={loading}
            className="bg-red-600 text-white px-5 py-2 rounded text-sm hover:bg-red-700 disabled:opacity-50"
          >
            {loading ? "Saving..." : "SAVE"}
          </button>
        </form>
      </div>
    </div>
  );
};

export default AddressForm;
